import React from 'react';
import { X } from 'lucide-react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * 매스컨셉 칩 — 이 저장소가 개념을 보여주는 정본 모양.
 *
 *   [순번][유형] 개념명
 *
 * 순번(order)과 유형(concept_type)을 작은 사각 배지로 앞에 두고, 이름은 LaTeX로 렌더한다.
 * 칩 몸통은 흰 바탕에 회색 테두리 — 색은 두 배지 안에만 들어간다.
 * 이 조합이 문제 미리보기·문제 상세·AI 트래커 등 8~10곳에서 같은 값으로 반복돼 왔다.
 * 정본으로 삼은 것: src/components/StudentsHub/ProblemPreviewPanel.jsx(제거 가능한 칩의 선례).
 *
 * 같은 마크업이 열다섯 파일에 복붙돼 있고 getConceptTypeColor는 스물두 곳에서 재정의돼 있다.
 * 기존 것을 한꺼번에 옮기지는 않는다(그 자체가 큰 회귀다) — 새로 쓰는 자리는 여기를 쓴다.
 *
 * @param {object} concept  fetchMathConcepts() 항목 { id, order, concept_type, concept_name|text }
 * @param {function} [onRemove]  주면 X 버튼이 붙는다
 * @param {function} [onClick]   주면 칩이 눌린다(보통 useMathceptDetail의 fetchAndShow)
 * @param {boolean} [disabled]   제거 버튼 잠금
 */

// 개념 유형 색 — 여덟 가지. 배경으로만 쓰므로 알파 0.5로 옅게 깔고 글자는 진회색을 얹는다.
export const CONCEPT_TYPE_COLOR = {
  DEF: 'rgba(239, 68, 68, 0.5)',
  AXM: 'rgba(249, 115, 22, 0.5)',
  IDN: 'rgba(245, 158, 11, 0.5)',
  THM: 'rgba(34, 197, 94, 0.5)',
  MTH: 'rgba(6, 182, 212, 0.5)',
  PRP: 'rgba(59, 130, 246, 0.5)',
  FOR: 'rgba(139, 92, 246, 0.5)',
  NTN: 'rgba(236, 72, 153, 0.5)',
};
const FALLBACK_TYPE_COLOR = 'rgba(156, 163, 175, 0.5)';
export const getConceptTypeColor = (type) => CONCEPT_TYPE_COLOR[type] || FALLBACK_TYPE_COLOR;

// 순번 배지 바탕 — 유형색과 같은 자리에 서므로 같은 규격의 무채색을 쓴다.
const ORDER_BG = 'rgba(156, 163, 175, 0.3)';
// 두 배지의 규격. 숫자·약어 2~3글자가 흔들리지 않게 폭을 고정한다.
const BADGE_BOX = { borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px' };

// 개념명에 섞인 $...$ 구간만 수식으로 바꾼다. katex 출력이라 dangerouslySetInnerHTML이 안전하다
// (사용자 입력이 아니라 개념 카탈로그 값이다).
export function renderConceptLatex(text) {
  if (!text) return '';
  try {
    const re = /\$([^$]+)\$/g;
    const parts = [];
    let last = 0;
    let m = re.exec(text);
    while (m !== null) {
      if (m.index > last) parts.push(text.substring(last, m.index));
      parts.push(`<span class="katex-inline">${katex.renderToString(m[1], { throwOnError: false, displayMode: false })}</span>`);
      last = m.index + m[0].length;
      m = re.exec(text);
    }
    if (last < text.length) parts.push(text.substring(last));
    return parts.join('');
  } catch (e) {
    return text; // 수식이 깨져도 이름은 보여야 한다
  }
}

function ConceptChip({ concept, onRemove, onClick, disabled = false }) {
  if (!concept) return null;
  const name = concept.concept_name || concept.text || `개념 ${concept.id ?? ''}`;
  const clickable = typeof onClick === 'function';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-3xl border border-gray-200 px-2 py-0.5 text-xs text-gray-700 ${
        clickable ? 'cursor-pointer transition-colors hover:bg-gray-50' : ''
      }`}
      onClick={clickable ? () => onClick(concept.id) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => {
        // 자기 자신에서 난 키만 처리한다. 안쪽 제거 버튼에서 올라온 Enter까지 preventDefault 하면
        // 버튼의 기본 동작(click 생성)이 취소돼, 키보드로는 개념을 지울 수 없고 상세만 열렸다.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(concept.id); }
      } : undefined}
    >
      {concept.order != null && (
        <span
          className="inline-flex items-center justify-center font-semibold text-gray-700"
          style={{ ...BADGE_BOX, backgroundColor: ORDER_BG, fontSize: '9px' }}
        >
          {concept.order}
        </span>
      )}
      {concept.concept_type && (
        <span
          className="inline-flex items-center justify-center font-semibold text-gray-800"
          style={{ ...BADGE_BOX, backgroundColor: getConceptTypeColor(concept.concept_type), fontSize: '8px' }}
        >
          {concept.concept_type}
        </span>
      )}
      {/* eslint-disable-next-line react/no-danger */}
      <span dangerouslySetInnerHTML={{ __html: renderConceptLatex(name) }} />
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(concept.id); }}
          disabled={disabled}
          title="개념 제거"
          aria-label={`개념 ${concept.id} 제거`}
          className="ml-0.5 hover:opacity-70 disabled:opacity-40"
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  );
}

export default ConceptChip;
